// @flow
import React, { useCallback, useState, useEffect } from 'react';
import { SearchInput, Paragraph } from 'operation-diameter';

import { searchCompanies } from 'shared/requests/searchCompanies';
import { logger } from 'shared/utils/logger';

import { CompanyType } from '../../utils/constants';
import type { Company, CompanyFromApi } from '../../utils/types';
import styles from './CompanySearchInput.module.css';

const { NODE_ENV } = process.env;

const IS_TEST = NODE_ENV === 'test';
const MIN_SEARCH_LENGTH = 3;
const SEARCH_INPUT_NAME = 'company-information';

const companyHasAllData = ({
  company_name,
  address,
  company_number,
  company_codas_id
}: CompanyFromApi): boolean =>
  !!(company_name && address && company_number && company_codas_id);

type SearchItem = {
  id: string,
  heading: string,
  description: string,
  uniqueCompanyIdentifier: string
};

const mapCompanyFromApiToSearchItem = ({
  company_name,
  address,
  company_number,
  company_codas_id,
  postcode
}: CompanyFromApi): SearchItem => ({
  id: company_codas_id,
  value: company_name,
  label: `${address} ${postcode}`,
  uniqueCompanyIdentifier: company_number
});

const mapSearchItemToCompany = ({
  id,
  value,
  uniqueCompanyIdentifier
}: SearchItem) => ({
  companyCodasId: id,
  companyName: value,
  uniqueCompanyIdentifier
});

type Props = {
  companyType: $Values<typeof CompanyType>,
  error: boolean,
  onBlur: Function,
  selectedCompany: Company | null,
  setSelectedCompany: (company: Company | null) => void,
  label: string,
  heading: string,
  helpText: string
};

const CompanySearchInput = ({
  companyType,
  error,
  onBlur,
  selectedCompany,
  setSelectedCompany,
  label,
  heading,
  helpText = 'Can’t find your business? Call us on 0800 048 2467'
}: Props) => {
  const [companies, setCompanies] = useState<SearchItem[]>([]);

  const [inputValue, setInputValue] = useState(
    selectedCompany ? selectedCompany.companyName : ''
  );
  const [prevInputValue, setPrevInputValue] = useState(inputValue);

  const setStringInputValue = (value: ?string) => {
    if (typeof value !== 'string') return;

    if (value === '') {
      setSelectedCompany(null);
    }

    setPrevInputValue(inputValue);
    setInputValue(value);
  };

  useEffect(() => {
    if (!inputValue.includes(prevInputValue)) {
      setCompanies([]);
    }

    if (!inputValue || inputValue.length < 3) {
      return;
    }

    const [response, abort] = searchCompanies(inputValue, companyType);

    if (!IS_TEST) {
      logger.log(`Sending request to ${inputValue}`);
    }

    response
      .then((apiCompanies: CompanyFromApi[]) => {
        const formattedCompanies = apiCompanies.reduce((acc, company) => {
          if (companyHasAllData(company)) {
            acc.push(mapCompanyFromApiToSearchItem(company));
          }
          return acc;
        }, []);

        setCompanies(formattedCompanies);
      })
      .catch((err) => {
        logger.warn(`Error fetching company ${inputValue}`, err);
      });

    return abort;
  }, [inputValue, prevInputValue, companyType]);

  const onSelectedCompanyChange = useCallback(
    (item) => {
      const input = document.querySelector(
        `input[name="${SEARCH_INPUT_NAME}"]`
      );
      if (input) {
        input.blur();
      }
      if (!item) {
        setSelectedCompany(null);
        return;
      }
      setSelectedCompany(mapSearchItemToCompany(item));
    },
    [setSelectedCompany]
  );

  return (
    <SearchInput
      label={label}
      heading={heading}
      name={SEARCH_INPUT_NAME}
      onChange={setStringInputValue}
      onBlur={onBlur}
      isFilteringDisabled={false}
      searchBy={(option) => [
        option.label,
        option.value,
        option.uniqueCompanyIdentifier
      ]}
      options={companies}
      onOptionSelect={onSelectedCompanyChange}
      initialOptionLabel={inputValue}
      helpText={helpText}
      setValue={(option) => option.value}
      renderCustomOption={(option, isSelected) => (
        <div
          className={`${styles.dropdown} ${styles.dropdownOption} ${
            isSelected ? styles.dropdownOptionSelected : ''
          }`}>
          <Paragraph paragraphWeight='600' paragraphSize='p1'>
            {option.value}
          </Paragraph>
          <Paragraph>{option.uniqueCompanyIdentifier}</Paragraph>
          <Paragraph paragraphSize='p2'>{option.label}</Paragraph>
        </div>
      )}
      {...(error && {
        errorMessage:
          inputValue?.length > MIN_SEARCH_LENGTH
            ? 'Company not found'
            : 'Please select a company'
      })}
    />
  );
};

export default CompanySearchInput;
